import { inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { catchError, exhaustMap, map, mergeMap, of, switchMap, withLatestFrom } from 'rxjs'
import { UserService } from '../../services/user.service'
import {
  blockEmployee,
  blockEmployeeFailure,
  blockEmployeeSuccess,
  createEmployee,
  createEmployeeFailure,
  createEmployeeSuccess,
  deleteEmployee,
  deleteEmployeeFailure,
  deleteEmployeeSuccess,
  loadEmployeeById,
  loadEmployeeByIdFailure,
  loadEmployeeByIdSuccess,
  loadEmployees,
  loadEmployeesFailure,
  loadEmployeesSuccess,
  loadUserById,
  loadUserByIdSuccess,
  loadUserFailure,
  loadUsers,
  loadUsersSuccess,
  portalAccessEmployee,
  portalAccessEmployeeFailure,
  portalAccessEmployeeSuccess,
  updateEmployee,
  updateEmployeeFailure,
  updateEmployeeSuccess
} from './user.actions'
import { Store } from '@ngrx/store'
import { ToastrService } from 'ngx-toastr'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { User } from '../auth/auth.state'
import { updateAuthUser } from '../auth/auth.actions'
import { selectUser } from '../auth/auth.selectors'
import { Department } from '../department/department.state'
import { Designation } from '../designation/designation.state'

export const loadUsers$ = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(loadUsers),
      switchMap(() =>
        userService.getUsers().pipe(
          map(response => loadUsersSuccess({ users: response.users })),
          catchError(error => of(loadUserFailure({ error: error.message })))
        )
      )
    ),
  { functional: true }
)

export const loadEmployees$ = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(loadEmployees),
      switchMap(
        (
          action // Use action.filter here
        ) =>
          userService.getEmployees(action.params, action.page, action.limit).pipe(
            map(res =>
              loadEmployeesSuccess({
                totalCount: res.totalCount,
                activeCount: res.activeCount,
                inactiveCount: res.inactiveCount,
                newJoineeCount: res.newJoineeCount,

                employees: res.users
              })
            ),
            catchError(error => of(loadEmployeesFailure({ error: error.message })))
          )
      )
    ),
  { functional: true }
)

export const blockEmployee$ = createEffect(
  (
    actions$ = inject(Actions),
    userService = inject(UserService),
    store = inject(Store),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(blockEmployee),
      exhaustMap(action =>
        userService.blockEmployee(action.id, action.disabled).pipe(
          map(res => {
            // const actionToDispatch =
            // console.log('Dispatching blockBusinessSuccess:', { business: res })
            // store.dispatch(actionToDispatch) // Dispatch the action
            //dispatch block  business success action
            if (action.disabled === true) {
              // Fixed typo here
              toastr.success('Employee inactive successfully!')
            } else {
              toastr.success('Employee active successfully!')
            }
            // store.dispatch(loadEmployees({ params: null, page: 1, limit: 10 }))
            return blockEmployeeSuccess({ user: res })
            // return blockBusinessSuccess({ business: res })
            // return blockBusinessSuccess({ id: action.id })
          }),
          catchError(error => {
            toastr.error('Failed to block business: ' + error.message)
            return of(blockEmployeeFailure({ error }))
          })
        )
      )
    ),
  { functional: true }
)

export const deleteEmployee$ = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService), toastr = inject(ToastrService)) =>
    actions$.pipe(
      ofType(deleteEmployee),
      exhaustMap(action =>
        userService.deleteEmployee(action.id).pipe(
          map(() => {
            toastr.success('Employee deleted successfully!')
            return deleteEmployeeSuccess({ id: action.id })
          }),
          catchError(error => {
            toastr.error('Failed to delete Employee: ' + error.message)
            return of(deleteEmployeeFailure({ error }))
          })
        )
      )
    ),
  { functional: true }
)

export const createEmployee$ = createEffect(
  (
    actions$ = inject(Actions),
    userService = inject(UserService),
    toastr = inject(ToastrService),
    store = inject(Store),
    modalService = inject(NgbModal)
  ) =>
    actions$.pipe(
      ofType(createEmployee),
      exhaustMap(({ payload }) => {
        const formData = new FormData()
        formData.append('firstName', payload.companyInfoForm.firstName)
        formData.append('lastName', payload.companyInfoForm.lastName)
        formData.append('email', payload.companyInfoForm.email)
        // if (payload.companyInfoForm.personalEmail?.trim()) {
        //   formData.append('personalEmail', payload.companyInfoForm.personalEmail)
        // }

        // if (payload.companyInfoForm.personalMobile?.trim()) {
        //   formData.append('personalMobile', payload.companyInfoForm.personalMobile)
        // }
        formData.append('mobile', payload.companyInfoForm.mobile)
        // formData.append('birthdate', payload.companyInfoForm.birthdate)
        formData.append('joiningDate', payload.companyInfoForm.joiningDate)
        formData.append('businessId', payload.companyInfoForm.businessId)
        formData.append('officeId', payload.companyInfoForm.officeId)
        formData.append('designationId', payload.companyInfoForm.designationId)
        // formData.append('empId', payload.companyInfoForm.empId)
        formData.append('gender', payload.companyInfoForm.gender)
        formData.append('role', payload.companyInfoForm.role)
        formData.append('portalAccess', payload.companyInfoForm.portalAccess)

        if (payload.companyInfoForm.picture) {
          formData.append('picture', payload.companyInfoForm.picture)
        }

        // 🔥 RETURN the inner observable here
        return userService.createEmployee(formData).pipe(
          map(created => {
            toastr.success('Employee created successfully!', 'Success')
            store.dispatch(loadEmployees({ params: null, page: 1, limit: 10 }))
            modalService.dismissAll()
            return createEmployeeSuccess({ user: created })
          }),
          catchError(error => {
            toastr.error(error.error.message, 'Error')
            return of(createEmployeeFailure({ error }))
          })
        )
      })
    ),
  { functional: true, dispatch: true }
)

export const loadEmployeeById$ = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService), toastr = inject(ToastrService)) =>
    actions$.pipe(
      ofType(loadEmployeeById),
      mergeMap(({ id }) =>
        userService.getEmployeeById(id).pipe(
          map(user => loadEmployeeByIdSuccess({ user })),
          catchError(error => {
            toastr.error(error.error.message, 'Error')

            return of(loadEmployeeByIdFailure({ error: error.message, statusCode: error.status }))
          })
        )
      )
    ),
  { functional: true }
)

// export const updateEmployee$ = createEffect(
//   (actions$ = inject(Actions), userService = inject(UserService), toastr = inject(ToastrService),  store = inject(Store)) =>
//     actions$.pipe(
//       ofType(updateEmployee),
//       mergeMap(({ id, data }) =>
//         userService.updateEmployee({ id, data }).pipe(
//           map(user => {
//             toastr.success('Employee updated successfully!', 'Success')
//             // Make Some Changes BY SHIVAM
//             // NgRx Store to trigger an action that reloads the employees list.
//             store.dispatch(loadEmployees({ params: null, page: 1, limit: 10 }))
//             return updateEmployeeSuccess({ user })
//           }),
//           catchError(error => {
//             toastr.error(error.error.message, 'Error')
//             return of(updateEmployeeFailure({ error }))
//           })
//         )
//       )
//     ),
//   { functional: true, dispatch: true }
// )

// THIS CODE IS WRITTEN BY SHIVAN
// export const updateEmployee$ = createEffect(
//   (
//     actions$ = inject(Actions),
//     userService = inject(UserService),
//     toastr = inject(ToastrService),
//     store = inject(Store)
//   ) =>
//     actions$.pipe(
//       ofType(updateEmployee),
//       exhaustMap(({ id, data }) =>
//         userService.updateEmployee({ id, data }).pipe(
//           map(user => {
//             toastr.success('Employee updated successfully!', 'Success')

//             // Update logged-in user in Auth Store
//             store.dispatch({
//               type: '[Auth] Update User',
//               user: user
//             })

//             return updateEmployeeSuccess({ user })
//           }),
//           catchError(error => {
//             toastr.error(error.error.message, 'Error')
//             return of(updateEmployeeFailure({ error }))
//           })
//         )
//       )
//     ),
//   { functional: true, dispatch: true }
// )

export const updateEmployee$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    userService = inject(UserService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(updateEmployee),
      withLatestFrom(store.select(selectUser)), // ✅ get logged-in user
      exhaustMap(([{ id, data }, currentUser]) =>
        userService.updateEmployee({ id, data }).pipe(
          map(user => {
            toastr.success('Employee updated successfully!', 'Success')
            // Check if updated user is logged-in user
            if (user.id === currentUser?.id) {
              let updatedDesignation = currentUser.designation

              if (user.designation?.department) {
                //  Pick a safe department (new one preferred, else old one)
                const mergedDepartment =
                  user.designation.department ?? currentUser.designation?.department

                if (mergedDepartment) {
                  updatedDesignation = {
                    ...currentUser.designation,
                    ...user.designation,
                    designationName:
                      user.designation.designationName ?? currentUser.designation?.designationName,
                    department: {
                      ...currentUser.designation?.department,
                      ...mergedDepartment,
                      departmentName:
                        mergedDepartment.departmentName ??
                        currentUser.designation?.department?.departmentName
                    } as Department
                  } as Designation
                }
              }

              // Only update selected fields
              const updatedAuthUser = {
                ...currentUser,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                mobile: user.mobile,
                picture: user.picture,
                joiningDate: user.joiningDate,
                office: user.office,
                designation: updatedDesignation
              }
              // Update Auth Store
              store.dispatch(updateAuthUser({ user: updatedAuthUser }))

              // Persist to localStorage
              localStorage.setItem('user', JSON.stringify(updatedAuthUser))
            }

            return updateEmployeeSuccess({ user })
          }),
          catchError(error => {
            toastr.error('There was an error updating the employee.', 'Error')
            return of(updateEmployeeFailure({ error }))
          })
        )
      )
    ),
  { functional: true, dispatch: true }
)

export const loadUserById$ = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(loadUserById),
      switchMap(({ id }) =>
        userService.getUser(id).pipe(
          map((user: User) => loadUserByIdSuccess({ user: user })),
          catchError(error => of(loadUserFailure({ error: error.message })))
        )
      )
    ),
  { functional: true }
)

export const portalAccessEmployee$ = createEffect(
  (
    actions$ = inject(Actions),
    userService = inject(UserService),
    store = inject(Store),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(portalAccessEmployee),
      exhaustMap(action =>
        userService.portalAccessEmployee(action.id, action.portalAccess).pipe(
          map(res => {
            // const actionToDispatch =
            // console.log('Dispatching blockBusinessSuccess:', { business: res })
            // store.dispatch(actionToDispatch) // Dispatch the action
            //dispatch block  business success action
            if (action.portalAccess === true) {
              // Fixed typo here
              toastr.success('Portal access given successfully!')
            } else {
              toastr.success('Portal access revoked successfully!')
            }
            // store.dispatch(loadEmployees({ params: null, page: 1, limit: 10 }))
            return portalAccessEmployeeSuccess({ user: res })
            // return blockBusinessSuccess({ business: res })
            // return blockBusinessSuccess({ id: action.id })
          }),
          catchError(error => {
            toastr.error(error.message)
            return of(portalAccessEmployeeFailure({ error }))
          })
        )
      )
    ),
  { functional: true }
)

export const userEffects = {
  loadUsers$,
  loadEmployees$,
  blockEmployee$,
  deleteEmployee$,
  createEmployee$,
  loadEmployeeById$,
  updateEmployee$,
  loadUserById$,
  portalAccessEmployee$
}
